import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import { useTranslation } from 'react-i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IClientModuleConfiguration,
  IUpdateClientModuleConfiguration,
} from './types';

export const useClientModuleConfiguration = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IClientModuleConfiguration, DefaultError>({
    queryKey: ['client-module-configuration'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Configuration/GetClientModuleConfiguration',
      });

      return responseHandler(response, {
        groupName: 'useClientModuleConfiguration',
      });
    },
  });
};

export const useUpdateClientModuleConfiguration = () => {
  const { t } = useTranslation();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useUpdateClientModuleConfiguration',
  );

  return useMutation<
    IClientModuleConfiguration,
    DefaultError,
    IUpdateClientModuleConfiguration
  >({
    mutationFn: async body => {
      loading(
        t('service-message.update.loading', {
          what: t('setting.client-module.what'),
        }),
      );

      const response = await apiClient({
        method: 'POST',
        url: 'Configuration/SetClientModuleConfiguration',
        body: {
          ...body,
          clientAccountId: 'f0af3888-ca5c-4309-bebd-4cce2614f066',
        },
      });

      return responseHandler(response, {
        groupName: 'useUpdateClientModuleConfiguration',
      });
    },
    onSuccess: data => {
      success(t('toast-message.settings-has-been-updated'));
    },
    onError: dismiss,
  });
};

export const useUpdateClientModuleLogo = () => {
  const { t } = useTranslation();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useUpdateClientModuleLogo',
  );
  return useMutation<unknown, DefaultError, { file: File; id: string }>({
    mutationFn: async body => {
      loading(
        t('service-message.update.loading', {
          what: t('setting.client-module.logo-what'),
        }),
      );
      const formData = new FormData();

      formData.append('file', body.file);

      const response = await apiClient<FormData>({
        method: 'POST',
        url: `Configuration/UploadLogo`,
        body: formData,
        params: { id: body.id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'useUpdateClientModuleLogo',
      });
    },
    onSuccess: () => {
      success(
        t('service-message.update.success', {
          what: t('setting.client-module.logo-what'),
        }),
      );
    },
    onError: dismiss,
  });
};

export const useUpdateClientModuleFavicon = () => {
  const { t } = useTranslation();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useUpdateClientModuleFavicon',
  );
  return useMutation<unknown, DefaultError, { file: File; id: string }>({
    mutationFn: async body => {
      loading(
        t('service-message.update.loading', {
          what: t('setting.client-module.favicon-what'),
        }),
      );
      const formData = new FormData();

      formData.append('file', body.file);

      const response = await apiClient<FormData>({
        method: 'POST',
        url: `Configuration/UploadFavicon`,
        body: formData,
        params: { id: body.id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'useUpdateClientModuleFavicon',
      });
    },
    onSuccess: () => {
      success(
        t('service-message.update.success', {
          what: t('setting.client-module.favicon-what'),
        }),
      );
    },
    onError: dismiss,
  });
};
